/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/hooks/useManagerHub.ts
 * PURPOSE: TanStack Query hooks cho Manager Hub — Release v0 WP-2 / SC-010-HUB-BM
 *          + 2026-08-17: ISS-MGR-11 (§27) — hook mới useManagerHoursSummary
 *          (widget [4] Tổng giờ làm, DD-0093)
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-08-17
 * COMPLIANCE: SDLC 6.5.0 + Sprint Governance Rule 3 (Zero Mock Tolerance)
 * PILLAR: P2 (Sprint Governance) + P4 (Quality Gates)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance | Rule 7: Performance SLAs (<50ms p95)
 *
 * @business-context
 * - Domain: Vietnamese F&B Workforce Management — Manager Hub (Tổng quan)
 * - Impact: Revenue Critical — độ phủ khu vực + lịch tuần + nhắc chấm công
 * - Stakeholders: Branch Managers, Shift Managers, Head Chefs
 *
 * @technical-architecture
 * - Layer: Custom Hooks (Business Logic)
 * - Pattern: TanStack Query v5 (useQuery, useMutation, useQueryClient)
 * - Dependencies: @tanstack/react-query, managerApi
 *
 * @cross-references
 * - API Service: frontend/src/services/api/managerApi.ts
 * - Backend: backend/app/api/v2/endpoints/manager_hub.py
 * - Page: frontend/src/pages/manager/ManagerHubPage.tsx
 * - Sibling pattern: frontend/src/hooks/useManagerDashboard.ts
 * ===============================================================================
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { managerApi, type RemindMissingRequest } from "../services/api/managerApi";

export const MANAGER_HUB_QUERY_KEYS = {
  coverageToday: ["manager", "hub", "coverage-today"] as const,
  weekSummary: ["manager", "hub", "week-summary"] as const,
  hoursSummary: (period: "week" | "month") => ["manager", "hub", "hours-summary", period] as const,
};

/** Widget [3] Độ phủ theo khu vực · Hôm nay */
export function useManagerCoverageToday() {
  return useQuery({
    queryKey: MANAGER_HUB_QUERY_KEYS.coverageToday,
    queryFn: () => managerApi.getCoverageToday(),
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: 2,
  });
}

/** Widget [2] Lịch tuần (gọn) — tuần hiện tại + status + cảnh báo */
export function useManagerWeekSummary() {
  return useQuery({
    queryKey: MANAGER_HUB_QUERY_KEYS.weekSummary,
    queryFn: () => managerApi.getWeekSummary(),
    refetchInterval: 120_000,
    staleTime: 60_000,
    retry: 2,
  });
}

/** Widget MỚI [4] Tổng giờ làm (ISS-MGR-11 / DD-0093) — tuần|tháng, per nhân viên */
export function useManagerHoursSummary(period: "week" | "month") {
  return useQuery({
    queryKey: MANAGER_HUB_QUERY_KEYS.hoursSummary(period),
    queryFn: () => managerApi.getHoursSummary({ period }),
    staleTime: 60_000,
    retry: 2,
  });
}

/** Dialog [N] Gửi nhắc chấm công — invalidate coverage sau khi nhắc */
export function useRemindMissing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: RemindMissingRequest) => managerApi.remindMissing(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["manager", "hub"] });
    },
  });
}
